import {z} from 'zod';
const ids=z.array(z.number().int().positive()).max(50000);
export const sidebarOrderSchema=z.object({version:z.literal(1),folders:ids,chats:z.record(ids)}).strict();
export type SidebarOrder=z.infer<typeof sidebarOrderSchema>;
export const emptySidebarOrder=():SidebarOrder=>({version:1,folders:[],chats:{}});
export const sidebarMoveSchema=z.object({kind:z.enum(['folder','chat']),id:z.number().int().positive(),targetId:z.number().int().positive(),position:z.enum(['before','after'])}).strict().refine(value=>value.id!==value.targetId,'Choose a different destination');
export type SidebarMove=z.infer<typeof sidebarMoveSchema>;
export type SidebarItem={id:number;parentId?:number|null;parent_id?:number|null;isPinned?:boolean;is_pinned?:boolean;createdAt?:string|Date|null;created_at?:string|Date|null};
export const sidebarParent=(item:SidebarItem)=>item.parentId??item.parent_id??null;
export const sidebarGroup=(parent:number|null)=>parent===null?'root':String(parent);
/** Missing/new rows retain their natural order ahead of the manually ordered
 * rows. Stale IDs never add rows; every caller keeps its own owned row set. */
export function orderSidebarRows<T extends {id:number}>(rows:readonly T[],saved:readonly number[]=[]):T[]{
 const rank=new Map(saved.map((id,index)=>[id,index]));
 return rows.map((row,index)=>({row,index})).sort((a,b)=>{
  const ar=rank.get(a.row.id),br=rank.get(b.row.id);
  return ar===undefined?(br===undefined?a.index-b.index:-1):br===undefined?1:ar-br;
 }).map(item=>item.row);
}
export function moveSidebarId(siblings:readonly number[],move:SidebarMove):number[]{
 if(!siblings.includes(move.id)||!siblings.includes(move.targetId))throw Error('Both rows must be siblings');
 const next=siblings.filter(id=>id!==move.id),at=next.indexOf(move.targetId)+(move.position==='after'?1:0);next.splice(at,0,move.id);return next;
}
export function orderSidebarFolders<T extends SidebarItem>(rows:readonly T[],order:SidebarOrder):T[]{
 return orderSidebarRows(rows,order.folders);
}
export function orderSidebarChats<T extends SidebarItem>(rows:readonly T[],order:SidebarOrder):T[]{
 const groups=new Map<string,T[]>();for(const row of rows){const key=sidebarGroup(sidebarParent(row));groups.set(key,[...(groups.get(key)??[]),row]);}
 for(const [key,items] of Array.from(groups)){
  // The all-projects endpoint is activity-sorted, while a folder's task endpoint
  // is creation-sorted. Use the folder order in both surfaces before applying a
  // saved order, so the first drag cannot unexpectedly reorder other siblings.
  const chronological=items.every(row=>Number.isFinite(Date.parse(String(row.createdAt??row.created_at??''))))
   ? [...items].sort((a,b)=>Date.parse(String(b.createdAt??b.created_at))-Date.parse(String(a.createdAt??a.created_at))||b.id-a.id):items;
  groups.set(key,orderSidebarRows(chronological,order.chats[key]));
 }
 const positions=new Map<string,number>();return rows.map(row=>{const key=sidebarGroup(sidebarParent(row)),position=positions.get(key)??0;positions.set(key,position+1);return groups.get(key)![position];});
}
